/**
 * ShareCard Component - Phase 8: Viral / PR Integration
 * Generates shareable content cards for social media
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Share2, MessageCircle, Twitter, Facebook, Copy, Check, Download, Hotel, Gift, Crown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pre-built share templates
const SHARE_TEMPLATES = {
  savings: {
    title: "I saved €{{amount}} on my hotel booking!",
    text: "Just booked my hotel through FreeStays and saved €{{amount}}! The room was FREE - I only paid for meals. Check it out: https://freestays.eu",
    hashtags: ["FreeStays", "FreeHotelRoom", "TravelHack"]
  },
  pass: {
    title: "My secret to FREE hotel rooms",
    text: "Got my FreeStays Pass and now all my hotel rooms are FREE! 🏨✨ Just pay for meals. Join the movement: https://freestays.eu",
    hashtags: ["FreeStays", "TravelTips", "HotelHack"]
  },
  booking: {
    title: "Booked {{hotel}} for FREE!",
    text: "Just booked {{hotel}} in {{destination}} and my room is FREE! 🎉 FreeStays makes hotel booking so much better. https://freestays.eu",
    hashtags: ["FreeStays", "Travel", "{{destination}}"]
  },
  referral: {
    title: "Get up to 50% Cash Back on hotel bookings!",
    text: "Use my FreeStays code {{code}} and get up to 50% Cash Back when you buy a pass! 🏨💰 FREE hotel rooms await: https://freestays.eu",
    hashtags: ["FreeStays", "CashBack", "TravelDeal"]
  }
};

const ShareCard = ({ 
  type = 'savings', 
  data = {}, 
  onShare,
  compact = false,
  userId = null
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const template = SHARE_TEMPLATES[type] || SHARE_TEMPLATES.savings;
  
  // Replace placeholders in template
  const processTemplate = (text) => {
    let processed = text;
    Object.entries(data).forEach(([key, value]) => {
      processed = processed.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return processed;
  };

  const shareText = processTemplate(template.text);
  const shareTitle = processTemplate(template.title);
  const hashtags = template.hashtags.map(h => processTemplate(h)).join(',');

  // Track share analytics
  const trackShare = async (method) => {
    try {
      await axios.post(`${API}/analytics/share`, {
        share_type: type,
        share_method: method,
        context: data,
        user_id: userId
      });
    } catch (err) {
      // Silent fail - don't block sharing
      console.log('Share tracking failed:', err);
    }
    onShare?.(method);
  };

  const shareToWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
    trackShare('whatsapp');
  };

  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&hashtags=${hashtags}`, '_blank');
    trackShare('twitter');
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=https://freestays.eu&quote=${encodeURIComponent(shareText)}`, '_blank');
    trackShare('facebook');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    toast.success(t('share.copied', 'Copied to clipboard!'));
    setTimeout(() => setCopied(false), 2000);
    trackShare('copy');
  };

  // Use native share if available
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: 'https://freestays.eu'
        });
        trackShare('native');
      } catch (err) {
        // User cancelled or error
      }
    } else {
      setDialogOpen(true);
    }
  };

  if (compact) {
    return (
      <Button 
        variant="outline" 
        size="sm" 
        className="rounded-full"
        onClick={nativeShare}
      >
        <Share2 className="w-4 h-4 mr-2" />
        {t('share.share', 'Share')}
      </Button>
    );
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="rounded-full gap-2"
          onClick={(e) => {
            if (navigator.share) {
              e.preventDefault();
              nativeShare();
            }
          }}
        >
          <Share2 className="w-4 h-4" />
          {t('share.shareSuccess', 'Share Your Success')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            {t('share.shareTitle', 'Share with Friends')}
          </DialogTitle>
        </DialogHeader>
        
        {/* Preview Card */}
        <Card className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 border-[#00A9E0]/20">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
              {type === 'savings' && <Gift className="w-6 h-6 text-white" />}
              {type === 'pass' && <Crown className="w-6 h-6 text-white" />}
              {type === 'booking' && <Hotel className="w-6 h-6 text-white" />}
              {type === 'referral' && <Share2 className="w-6 h-6 text-white" />}
            </div>
            <div>
              <p className="font-semibold text-sm">{shareTitle}</p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{shareText}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 mt-3">
            {template.hashtags.map((tag, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                #{processTemplate(tag)}
              </Badge>
            ))}
          </div>
        </Card>

        {/* Share Buttons */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button 
            className="bg-green-500 hover:bg-green-600 text-white rounded-full"
            onClick={shareToWhatsApp}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
          <Button 
            className="bg-blue-400 hover:bg-blue-500 text-white rounded-full"
            onClick={shareToTwitter}
          >
            <Twitter className="w-4 h-4 mr-2" />
            Twitter
          </Button>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
            onClick={shareToFacebook}
          >
            <Facebook className="w-4 h-4 mr-2" />
            Facebook
          </Button>
          <Button 
            variant="outline"
            className="rounded-full"
            onClick={copyToClipboard}
          >
            {copied ? (
              <Check className="w-4 h-4 mr-2 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 mr-2" />
            )}
            {copied ? t('share.copied', 'Copied!') : t('share.copy', 'Copy')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareCard;
